import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import CircularProgress from "@material-ui/core/CircularProgress";
import SaveAltIcon from "@material-ui/icons/SaveAlt";
import { useMediaQuery } from "@mui/material";
import Table from "../invoiceslist/CurrentInvoicesTable";
import Alert from "../../alerts/Alert";
import ButtonGroupComponent from "../../reusableComponents/ButtonGroup";
import GetUserToken from "../../../utils/GetUserToken";
import { TrackableLink } from "../../../utils/helper";

const PatientInvoices = ({ patientId, deleteToken, history }) => {
  // auth
  const userToken = GetUserToken();
  // translations
  const { t } = useTranslation();
  let currentLanguage = localStorage.getItem("i18nextLng");
  // utils
  const [loading, setLoading] = useState(false);
  const [isAlert, setIsAlert] = useState({
    open: false,
    severity: "",
    message: "",
  });
  const isMobile = useMediaQuery("(max-width:600px)");
  // data
  const [invoices, setInvoices] = useState([]);
  // table
  const columns = useMemo(
    () => [
      {
        // first group
        Header: " ",
        // First group columns
        columns: [
          {
            Header: "ID",
            accessor: "id",
          },
          {
            Header: <>{t("PatientInvoices.Last Name")}</>,
            accessor: "lastname",
            Cell: ({ row }) => <>{JSON.parse(row.original.patient).lastname}</>,
          },
          {
            Header: <>{t("PatientInvoices.First Name")}</>,
            accessor: "firstname",
            Cell: ({ row }) => (
              <>{JSON.parse(row.original.patient).firstname}</>
            ),
          },
          {
            Header: <>{t("PatientInvoices.Created at")}</>,
            accessor: "date",
          },
          {
            Header: <>{t("PatientInvoices.Due to")}</>,
            accessor: "due_date",
            Cell: ({ row }) => (
              <>
                {row.original.due_date != null ? row.original.due_date : <></>}
              </>
            ),
          },
          {
            Header: <>{t("PatientInvoices.Total")}</>,
            accessor: "total",
            Cell: ({ row }) => (
              <>
                {row.original.total != null ? (
                  parseFloat(row.original.total).toFixed(2).replace(".", ",")
                ) : (
                  <>0,00</>
                )}
              </>
            ),
          },
          {
            Header: <>{t("PatientInvoices.Last status update")}</>,
            accessor: "updated_at",
            Cell: ({ row }) => <>{row.original.updated_at.split("T")[0]}</>,
          },
          {
            Header: <>{t("PatientInvoices.Status")}</>,
            accessor: "status",
            Cell: ({ row }) => (
              <>
                {row.original.status == "warning" ? (
                  <>
                    <span className="invoices-list-warning-badge">
                      &nbsp;&nbsp;<>{t("CurrentInvoices.6")}</>&nbsp;&nbsp;
                    </span>
                  </>
                ) : row.original.status == "cancel" ? (
                  <>
                    <span className="invoices-list-cancel-badge">
                      &nbsp;&nbsp;<>{t("CurrentInvoices.15")}</>&nbsp;
                      {row.original.paid_at.split("T")[0]}&nbsp;
                    </span>
                  </>
                ) : row.original.status == "paid" ? (
                  <>
                    <span className="invoices-list-invoiced-badge">
                      &nbsp;&nbsp;<>{t("CurrentInvoices.7")}</>&nbsp;
                      {row.original.paid_at.split("T")[0]}&nbsp;&nbsp;
                    </span>
                  </>
                ) : row.original.status == "paidBank" ? (
                  <>
                    <span className="invoices-list-invoiced-badge">
                      &nbsp;&nbsp;
                      <>{t("NewInvoicesStatus.Mark as paid bank transfer")}</>
                      &nbsp;{row.original.paid_at.split("T")[0]}&nbsp;
                    </span>
                  </>
                ) : row.original.status == "paidCash" ? (
                  <>
                    <span className="invoices-list-invoiced-badge">
                      &nbsp;&nbsp;
                      <>{t("NewInvoicesStatus.Mark as paid Cash")}</>
                      &nbsp;{row.original.paid_at.split("T")[0]}&nbsp;
                    </span>
                  </>
                ) : row.original.status == "paidOffice" ? (
                  <>
                    <span className="invoices-list-invoiced-badge">
                      &nbsp;&nbsp;
                      <>{t("NewInvoicesStatus.Mark as paid office")}</>
                      &nbsp;{row.original.paid_at.split("T")[0]}&nbsp;
                    </span>
                  </>
                ) : row.original.status == "paidOnline" ? (
                  <>
                    <span className="invoices-list-invoiced-badge">
                      &nbsp;&nbsp;
                      <>{t("NewInvoicesStatus.Mark as paid online")}</>
                      &nbsp;{row.original.paid_at.split("T")[0]}&nbsp;
                    </span>
                  </>
                ) : row.original.status == "partialpaid" ? (
                  <>
                    <span className="invoices-list-partialpaid-badge">
                      &nbsp;&nbsp;<>{t("CurrentInvoices.19")}</>
                      &nbsp;&nbsp;
                    </span>
                  </>
                ) : row.original.status == "unpaid" ? (
                  <>
                    <span className="invoices-list-unpaid-badge">
                      &nbsp;&nbsp;<>{t("CurrentInvoices.8")}</>
                      &nbsp;&nbsp;
                    </span>
                  </>
                ) : null}
              </>
            ),
          },
          {
            Header: <>{t("PatientInvoices.Download")}</>,
            accessor: " ",
            Cell: ({ row }) => (
              <span
                id="patient_downloadInvoice"
                style={{ cursor: "pointer" }}
                onClick={() => downloadInvoice(row.original.id)}
              >
                <SaveAltIcon
                  className="patientInvoices-download-icon"
                  fontSize="small"
                />
              </span>
            ),
          },
        ],
      },
    ],
    [currentLanguage]
  );

  useEffect(() => {
    getPatientInvoices();
  }, []);

  const getPatientInvoices = async () => {
    try {
      setLoading(true);
      var requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "api-key": process.env.REACT_APP_API_KEY,
          Authorization: userToken,
        },
        redirect: "follow",
      };

      const res = await fetch(
        `${process.env.REACT_APP_API_URL_V2}/invoices/get_invoices_for_patient/${patientId}`,
        requestOptions
      );

      if (res.ok) {
        const json = await res.json();
        setInvoices(json);
      } else {
        handleLogout();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoice = async (event) => {
    TrackableLink("patient_downloadInvoice");

    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "api-key": process.env.REACT_APP_API_KEY,
        Authorization: userToken,
      },
      redirect: "follow",
    };

    const url =
      process.env.REACT_APP_API_URL_V2 + "/invoices/generate/" + event;

    if (!event) {
      setIsAlert({
        open: true,
        severity: "warning",
        message: t("Messages.Error, please refresh and try again"),
      });
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(url, requestOptions);

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      // Convert the response to a blob
      const blob = await response.blob();

      // Create a temporary URL for the blob
      const downloadUrl = window.URL.createObjectURL(blob);

      // Create a temporary link element
      const link = document.createElement("a");
      link.href = downloadUrl;

      // Set the file name, adjust this depending on your backend response
      link.download = `RE_TCM_${event}.pdf`;
      // Programmatically click the link to trigger the download
      link.click();

      // Clean up the temporary URL
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("There was a problem with the fetch operation:", error);
      setIsAlert({
        open: true,
        severity: "error",
        message: t("Messages.Error, please refresh and try again"),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    history.push("/");
    deleteToken();
  };

  return (
    <>
      {/* Support */}
      <ButtonGroupComponent route="patientInvoices" />

      <div className="frame">
        <h4 className="main-title mt-2">{t("PatientInvoices.Invoices")}</h4>
        <br />
        <div style={{ marginRight: isMobile ? "0px" : "50px" }}>
          <div className="container-fluid treatments-table">
            {loading ? (
              <div className="d-flex justify-content-center">
                <CircularProgress color="secondary" />
              </div>
            ) : (
              <Table columns={columns} data={invoices} PatientInvoices />
            )}
          </div>
        </div>
      </div>

      {isAlert.open && (
        <Alert
          isAlert={isAlert}
          setIsAlert={setIsAlert}
          severity={isAlert.severity}
          message={isAlert.message}
        />
      )}
    </>
  );
};

export default PatientInvoices;
